<template>
    <form ref="teamForm" class="@container flex flex-col gap-6" @submit.prevent="createTeam()">
        <BaseButton
            v-if="embedded && cancelLabel && !showInvitationRecovery"
            theme="tertiary"
            size="sm"
            class="self-start"
            :disabled="submitting"
            @click="
                cancelCreation();
                emit('cancel');
            ">
            <i class="bi bi-chevron-left" aria-hidden="true" />{{ cancelLabel }}
        </BaseButton>
        <BaseButton
            v-if="recommendationsUrl && showDetails && !showInvitationRecovery"
            theme="tertiary"
            class="body-small text-ink-muted self-start p-0 hover:bg-transparent hover:underline"
            :disabled="draftLocked"
            @click="backToSuggestions">
            <i class="bi bi-chevron-left" aria-hidden="true" />{{ t("peopleDirectory.createTeam.backToSuggestions") }}
        </BaseButton>
        <BaseLink v-else-if="!embedded && !showInvitationRecovery" :href="backUrl" type="button" class="base-link body-small self-start" @click="cancelCreation">
            <i class="bi bi-chevron-left" aria-hidden="true" />{{ t("peopleDirectory.createTeam.back") }}
        </BaseLink>
        <header class="flex flex-col gap-6 @xl:flex-row @xl:items-start @xl:justify-between @xl:gap-10">
            <div class="flex grow flex-col gap-2">
                <component :is="embedded ? 'h3' : 'h1'" v-if="heading.title" :class="embedded ? 'nadia-chat-bold text-ink' : 'display-serif text-ink'">{{
                    heading.title
                }}</component>
                <div>
                    <p :class="['body text-ink-muted', { 'max-w-lg': recommendationsUrl && !showDetails }]">{{ heading.hint }}</p>
                    <p v-if="heading.note" class="body text-ink-muted">{{ heading.note }}</p>
                </div>
            </div>
            <BaseButton
                v-if="recommendationsUrl && !showDetails"
                theme="neutral"
                class="base-btn-with-icon hidden shrink-0 self-start @xl:inline-flex"
                :disabled="submitting"
                @click="reviewTeam">
                <i class="bi bi-plus-lg" aria-hidden="true" />{{ t("peopleDirectory.createTeam.manual") }}
            </BaseButton>
        </header>
        <section
            v-if="recommendationsUrl"
            v-show="!showDetails"
            ref="suggestionsScreen"
            tabindex="-1"
            class="flex flex-col gap-6"
            :aria-label="t('peopleDirectory.teamSuggestions.title')">
            <CalendarTeamSuggestions
                :source="source"
                :url="recommendationsUrl"
                :demo-tools-visible="!showDetails"
                :selected-member-ids="[creator.id, ...members.map((member) => member.id)]"
                :selected-unavailable-ids="unavailableMembers.map((member) => member.id)"
                :disabled="submitting"
                :create-disabled="selected.length >= 100"
                @join="joinSuggestion"
                @select="useSuggestion"
                @review="reviewSuggestion" />
            <p class="body-small text-ink empty:sr-only" role="alert">{{ joinError }}</p>
            <p class="body-small text-ink empty:sr-only" role="status">
                <template v-if="joiningName">{{ t("peopleDirectory.teamSuggestions.joining", { name: joiningName }) }}</template>
                <template v-else-if="joinedTeam">{{ t("peopleDirectory.teamSuggestions.joined", { name: joinedTeam.name }) }}</template>
            </p>
            <!-- Narrow forms (chat drawer, phones) offer manual entry after the suggestions, as wide as the cards; wide forms keep it in the header. -->
            <BaseButton theme="neutral" class="base-btn-with-icon w-full @xl:hidden" :disabled="submitting" @click="reviewTeam">
                <i class="bi bi-plus-lg" aria-hidden="true" />{{ t("peopleDirectory.createTeam.manual") }}
            </BaseButton>
        </section>
        <template v-if="showDetails">
            <section v-if="!showInvitationRecovery" class="flex flex-col gap-6" :aria-label="t('peopleDirectory.createTeam.detailsTitle')">
                <FormField :id="nameId" :label="t('peopleDirectory.createTeam.name')" required>
                    <BaseInput v-model="name" :placeholder="t('peopleDirectory.createTeam.namePlaceholder')" :maxlength="255" :disabled="draftLocked" required />
                </FormField>
                <section class="flex flex-col gap-3" :aria-label="t('peopleDirectory.createTeam.members')">
                    <FormField :id="searchId" :label="t('peopleDirectory.createTeam.searchLabel')" :note="t('peopleDirectory.createTeam.searchNote')" required>
                        <BaseCombobox
                            v-model="candidate"
                            v-model:input="query"
                            :clearable="false"
                            :ignore-filter="true"
                            :options="options"
                            :key-value="(person) => person.id"
                            :display-value="(person) => `${person.name} ${person.email}`"
                            :placeholder="t('peopleDirectory.createTeam.searchPlaceholder')"
                            :disabled="draftLocked || selected.length >= 100"
                            :actions="invitationActions"
                            @action="stageInvitation"
                            @keydown.enter.prevent>
                            <template #item="{ option }">
                                <div class="flex items-center gap-3 fs-mask">
                                    <PeopleAvatar :name="option.name" size="sm" />
                                    <div class="min-w-0">
                                        <p class="body-small-semibold text-ink truncate">{{ option.name }}</p>
                                        <p class="details text-ink-muted truncate">{{ option.email }}</p>
                                    </div>
                                </div>
                            </template>
                            <template #empty
                                ><p v-if="!options.length && !invitationActions.length && searchStatus" class="body-small p-3 text-ink-muted">{{ searchStatus }}</p></template
                            >
                        </BaseCombobox>
                    </FormField>
                    <p class="sr-only" role="status">
                        <template v-if="searchStatus">{{ searchStatus }}</template>
                    </p>
                    <div class="empty:sr-only" role="alert">
                        <template v-if="searchError">
                            <p class="body-small text-ink">{{ t("peopleDirectory.createTeam.searchError") }}</p>
                            <BaseButton theme="secondary" size="sm" class="mt-2" :disabled="isFetching" @click="execute()">
                                {{ t("peopleDirectory.retry") }}
                            </BaseButton>
                        </template>
                    </div>
                    <div class="empty:sr-only" role="alert">
                        <template v-if="capabilitiesError">
                            <p class="body-small text-ink">{{ t("teamInvitations.loadError") }}</p>
                            <BaseButton theme="secondary" size="sm" :disabled="capabilitiesLoading || draftLocked" @click="loadCapabilities()">{{
                                t("peopleDirectory.retry")
                            }}</BaseButton>
                        </template>
                    </div>
                    <BaseCard padding="none" radius="md">
                        <div class="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
                            <h2 class="body-small-semibold text-ink flex items-center gap-2">
                                {{ t("peopleDirectory.createTeam.members") }}<BaseCountBadge :count="selected.length + 1" tone="neutral" :ringed="false" aria-hidden="true" />
                            </h2>
                            <p class="sr-only" role="status">{{ t("peopleDirectory.createTeam.memberCount", { count: selected.length + 1 }) }}</p>
                        </div>
                        <ul class="divide-y divide-border @xl:grid @xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_auto]">
                            <li class="flex items-center gap-3 p-4 @xl:col-span-4 @xl:grid @xl:grid-cols-subgrid">
                                <PeopleAvatar :name="creator.name" size="xs" />
                                <div class="min-w-0 flex-1 @xl:contents">
                                    <p class="body-small-semibold text-ink truncate">{{ t("peopleDirectory.createTeam.you", { name: creator.name }) }}</p>
                                    <p class="body-small text-ink-muted truncate">{{ creator.email }}</p>
                                </div>
                            </li>
                            <li v-for="selection in selected" :key="selectionKey(selection)" class="flex items-center gap-3 p-4 @xl:col-span-4 @xl:grid @xl:grid-cols-subgrid">
                                <PeopleAvatar :name="selectionName(selection)" size="xs" />
                                <div class="min-w-0 flex-1 @xl:contents">
                                    <p class="body-small-semibold text-ink truncate">{{ selectionName(selection) }}</p>
                                    <p class="body-small text-ink-muted truncate">
                                        {{ selection.kind === "member" ? selection.person.email : t("peopleDirectory.createTeam.willBeInvited") }}
                                    </p>
                                </div>
                                <BaseButton
                                    theme="neutral"
                                    size="sm"
                                    icon-only
                                    :aria-label="t('peopleDirectory.createTeam.remove', { name: selectionName(selection) })"
                                    :disabled="draftLocked"
                                    @click="removeSelection(selection)">
                                    <i class="bi bi-x-lg" aria-hidden="true" />
                                </BaseButton>
                            </li>
                        </ul>
                    </BaseCard>
                    <BaseCard v-if="unavailableMembers.length" padding="sm" radius="md" theme="muted">
                        <section class="flex flex-col gap-3" :aria-label="t('peopleDirectory.createTeam.unavailableTitle')">
                            <h2 class="body-small-semibold text-ink">{{ t("peopleDirectory.createTeam.unavailableTitle") }}</h2>
                            <p class="body-small text-ink-muted">{{ t("peopleDirectory.createTeam.unavailableHint") }}</p>
                            <ul class="flex flex-col gap-3">
                                <li v-for="member in unavailableMembers" :key="member.id" class="flex items-center gap-3">
                                    <PeopleAvatar :name="member.name" size="sm" />
                                    <div class="min-w-0 flex-1">
                                        <p class="body-small-semibold text-ink break-words">{{ member.name }}</p>
                                        <p class="details text-ink-muted break-words">{{ member.email }}</p>
                                        <p class="details text-ink-muted">{{ t("peopleDirectory.teamSuggestions.unavailable") }}</p>
                                    </div>
                                    <BaseButton
                                        theme="neutral"
                                        size="sm"
                                        icon-only
                                        :aria-label="t('peopleDirectory.createTeam.remove', { name: member.name })"
                                        :disabled="draftLocked"
                                        @click="unavailableMembers = unavailableMembers.filter((person) => person.id !== member.id)">
                                        <i class="bi bi-x-lg" aria-hidden="true" />
                                    </BaseButton>
                                </li>
                            </ul>
                        </section>
                    </BaseCard>
                </section>
            </section>
            <BaseNotification theme="error" hide-dismiss class="empty:sr-only">
                <template #content>
                    <div v-if="submitError" class="flex flex-col gap-2">
                        <p class="body-small-semibold text-ink">{{ t("peopleDirectory.createTeam.errorTitle") }}</p>
                        <p class="body-small text-ink">{{ submitError }}</p>
                        <p class="body-small text-ink-muted">
                            {{ creation.kind === "pending" ? t("peopleDirectory.createTeam.retryUnconfirmed") : t("peopleDirectory.createTeam.errorHint") }}
                        </p>
                    </div>
                </template>
            </BaseNotification>
            <div class="flex flex-col gap-2 empty:sr-only" role="status">
                <template v-if="showInvitationRecovery && createdTeam">
                    <p class="body text-ink">{{ t("peopleDirectory.createTeam.created", { name: createdTeam.name }) }}</p>
                </template>
            </div>
            <section v-if="showInvitationRecovery" ref="invitationStep" tabindex="-1" class="flex flex-col gap-4" :aria-label="t('peopleDirectory.createTeam.inviteTitle')">
                <div v-if="invitations.length" class="flex flex-col gap-3">
                    <h2 class="body-small-semibold text-ink">{{ t("peopleDirectory.createTeam.remainingInvitations") }}</h2>
                    <ul class="flex flex-col gap-3">
                        <li v-for="invitation in invitations" :key="invitation.requestId" class="flex items-center gap-3">
                            <span class="body-small text-ink min-w-0 flex-1 break-words">{{ invitation.email }}</span>
                            <BaseButton
                                theme="neutral"
                                size="sm"
                                icon-only
                                :disabled="submitting"
                                :aria-label="t('peopleDirectory.createTeam.remove', { name: invitation.email })"
                                @click="removeSelection(invitation)">
                                <i class="bi bi-x-lg" aria-hidden="true" />
                            </BaseButton>
                        </li>
                    </ul>
                    <p v-if="!capabilities?.can_invite_accountless" class="body-small text-ink">{{ t("peopleDirectory.createTeam.emailDisabled") }}</p>
                    <BaseButton v-else theme="primary" class="self-start" :disabled="submitting" @click="retryInvitations">{{
                        invitationError ? t("peopleDirectory.createTeam.retryInvitations") : t("teamInvitations.sendInvites")
                    }}</BaseButton>
                </div>
                <p class="body-small text-ink empty:sr-only" role="alert">{{ invitationError }}</p>
                <div class="flex justify-end">
                    <BaseButton :theme="invitations.length && capabilities?.can_invite_accountless ? 'neutral' : 'primary'" :disabled="submitting" @click="finishCreation">{{
                        invitations.length ? t("peopleDirectory.createTeam.continueWithoutInvites") : t("peopleDirectory.createTeam.continue")
                    }}</BaseButton>
                </div>
            </section>
            <footer v-if="!showInvitationRecovery" class="flex flex-wrap items-center gap-3">
                <p class="body-small text-ink-muted empty:sr-only" role="status">
                    <template v-if="creating">{{ t("peopleDirectory.createTeam.creatingStatus") }}</template>
                </p>
                <div class="ml-auto flex flex-wrap justify-end gap-2">
                    <BaseLink v-if="!embedded" :href="backUrl" class="base-btn-neutral" @click="cancelCreation">{{ t("peopleDirectory.createTeam.cancel") }}</BaseLink>
                    <BaseButton theme="primary" type="submit" :disabled="!canCreate">{{
                        creating
                            ? t("peopleDirectory.createTeam.creating")
                            : creation.kind === "pending"
                              ? t("peopleDirectory.retry")
                              : invitations.length
                                ? t("peopleDirectory.createTeam.createAndSend")
                                : t("peopleDirectory.createTeam.submit")
                    }}</BaseButton>
                </div>
            </footer>
        </template>
    </form>
</template>

<script setup lang="ts">
import { refDebounced, type UseFetchReturn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { PersonTeam, TeamCreated, TeamCreateRequest } from "~vue/api/generated/product-teams";
import type { TeamInvitationCapabilities, TeamInvitationCreated, TeamInvitationCreateRequest, TeamJoinLinkCreated } from "~vue/api/generated/team-invitations";
import type {
    CalendarTeamMember,
    ExistingTeamRecommendation,
    TeamCandidate,
    TeamCandidateSearch,
    TeamPerspectiveSummary,
    TeamRecommendation,
} from "~vue/api/generated/team-perspective/types.gen";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCombobox, { type ComboboxAction } from "~vue/components/BaseCombobox.vue";
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import CalendarTeamSuggestions from "~vue/Connections/CalendarTeamSuggestions.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { TEAM_INTERACTION_SOURCE, teamInteractionRequest, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, useId, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { z } from "zod";

const {
    source = TEAM_INTERACTION_SOURCE.MY_PEOPLE,
    creator,
    searchUrl,
    createUrl,
    teamUrlTemplate,
    backUrl,
    recommendationsUrl = null,
    embedded = false,
    initialName = "",
    cancelLabel,
    titleShownAbove = false,
} = defineProps<{
    source?: TeamInteractionSource;
    creator: TeamCandidate;
    searchUrl: string;
    createUrl: string;
    teamUrlTemplate: string;
    backUrl: string;
    recommendationsUrl?: string | null;
    embedded?: boolean;
    initialName?: string;
    cancelLabel?: string;
    titleShownAbove?: boolean;
}>();
const emit = defineEmits<{ selected: [team: TeamPerspectiveSummary]; cancel: []; submitting: [value: boolean] }>();
const { t } = useI18n();
const teamForm = useTemplateRef<HTMLFormElement>("teamForm");
const formId = useId();
const nameId = embedded ? `${formId}-team-name` : "team-name";
const searchId = embedded ? `${formId}-team-member-search` : "team-member-search";
const name = ref(initialName);
const creationSource = ref(source);
const creationAttribution = computed(() => ({ source: creationSource.value, entry_point: source }));
const showDetails = ref(!recommendationsUrl);
const memberHint = computed(() =>
    capabilities.value?.can_invite_accountless ? t("peopleDirectory.createTeam.membersInvitationHint") : t("peopleDirectory.createTeam.membersHint"),
);
const heading = computed(() => {
    if (!recommendationsUrl) {
        return { title: titleShownAbove ? "" : t("peopleDirectory.createTeam.title"), hint: memberHint.value };
    }
    return showDetails.value
        ? {
              title: t("peopleDirectory.createTeam.detailsTitle"),
              hint: t("peopleDirectory.createTeam.detailsHint"),
              note: memberHint.value,
          }
        : { title: t("peopleDirectory.createTeam.suggestionsTitle"), hint: t("peopleDirectory.createTeam.suggestionsHint") };
});
const suggestionsScreen = useTemplateRef<HTMLElement>("suggestionsScreen");

async function backToSuggestions() {
    cancelCreation();
    showDetails.value = false;
    await nextTick();
    suggestionsScreen.value?.focus();
}

async function reviewTeam() {
    startCreation();
    showDetails.value = true;
    await nextTick();
    const input = teamForm.value?.querySelector<HTMLInputElement>(`[id="${nameId}"]`);
    input?.focus({ preventScroll: true });
    input?.scrollIntoView({ block: "center", behavior: "instant" });
}
type TeamSelection = { kind: "member"; person: TeamCandidate } | { kind: "invitation"; email: string; requestId: string; status: "pending" | "sent" };
type CreationState =
    | { kind: "draft" }
    | { kind: "pending"; payload: TeamCreateRequest }
    | { kind: "saved"; team: TeamPerspectiveSummary }
    | { kind: "completed"; team: TeamPerspectiveSummary };
const selected = ref<TeamSelection[]>([]);
const members = computed(() => selected.value.filter((selection) => selection.kind === "member").map((selection) => selection.person));
const invitations = computed(() => selected.value.filter((selection) => selection.kind === "invitation"));
const pendingInvitations = computed(() => invitations.value.filter((invitation) => invitation.status === "pending"));
const {
    data: capabilities,
    error: capabilitiesError,
    isFetching: capabilitiesLoading,
    execute: loadCapabilities,
} = useFetch("/v1/teams/invitation-capabilities").get().json<TeamInvitationCapabilities>();
function selectionKey(selection: TeamSelection): string {
    return selection.kind === "member" ? `member:${selection.person.id}` : `invitation:${selection.email}`;
}
function selectionName(selection: TeamSelection): string {
    return selection.kind === "member" ? selection.person.name : selection.email;
}
function selectedEmail(email: string): boolean {
    const normalized = email.trim().toLowerCase();
    return (
        creator.email.trim().toLowerCase() === normalized ||
        selected.value.some((selection) => (selection.kind === "member" ? selection.person.email : selection.email).trim().toLowerCase() === normalized)
    );
}
const unavailableMembers = ref<CalendarTeamMember[]>([]);
const candidate = ref<TeamCandidate | null>(null);
const query = ref("");
const debouncedQuery = refDebounced(query, 250);
const url = computed(() => `${searchUrl}?${new URLSearchParams({ q: debouncedQuery.value.trim() })}`);
const searchRequest = shallowRef<UseFetchReturn<TeamCandidateSearch>>();
const data = computed(() => searchRequest.value?.data.value);
const searchError = computed(() => searchRequest.value?.error.value);
const isFetching = computed(() => searchRequest.value?.isFetching.value ?? false);
watch(url, (search, _previous, onCleanup) => {
    searchRequest.value = undefined;
    if (debouncedQuery.value.trim().length < 2) {
        return;
    }
    // Keep each request's state separate so superseded responses cannot replace current matches.
    const request = useFetch(search, { immediate: false }).get().json<TeamCandidateSearch>();
    searchRequest.value = request;
    onCleanup(() => request.abort());
    void request.execute();
});
function execute() {
    return searchRequest.value?.execute();
}
const searching = computed(() => query.value !== debouncedQuery.value || isFetching.value);
const options = computed(() => {
    if (query.value.trim().length < 2 || searching.value || searchError.value) {
        return [];
    }
    const selected = new Set([creator.id, ...members.value.map((person) => person.id)]);
    return (data.value?.results ?? []).filter((person) => !selected.has(person.id) && !selectedEmail(person.email));
});
const unmatchedEmail = computed(() => {
    const email = query.value.trim().toLowerCase();
    return !searching.value &&
        !searchError.value &&
        data.value?.results.every((person) => person.email.trim().toLowerCase() !== email) &&
        !selectedEmail(email) &&
        z.string().email().safeParse(email).success
        ? email
        : "";
});
const invitationActions = computed<ComboboxAction[]>(() =>
    capabilities.value?.can_invite_accountless && unmatchedEmail.value && selected.value.length < 100 && !draftLocked.value
        ? [{ id: "invite-email", label: t("teamInvitations.addToEmailList", { email: unmatchedEmail.value }) }]
        : [],
);
function stageInvitation() {
    if (!invitationActions.value.length) {
        return;
    }
    selected.value.push({ kind: "invitation", email: unmatchedEmail.value, requestId: globalThis.crypto.randomUUID(), status: "pending" });
    query.value = "";
}
const searchStatus = computed(() => {
    if (selected.value.length >= 100) {
        return t("peopleDirectory.createTeam.limit");
    }
    const queryLength = query.value.trim().length;
    if (queryLength === 0) {
        return "";
    }
    // Only shown once someone starts typing, so the field itself stays free of a permanent hint.
    if (queryLength < 2) {
        return t("peopleDirectory.createTeam.searchHint");
    }
    if (searching.value) {
        return t("peopleDirectory.createTeam.searching");
    }
    if (searchError.value) {
        return "";
    }
    if (invitationActions.value.length) {
        return invitationActions.value[0]?.label ?? "";
    }
    return options.value.length ? t("peopleDirectory.createTeam.results", { count: options.value.length }) : t("peopleDirectory.createTeam.noMatches");
});
const submitting = ref(false);
const creation = ref<CreationState>({ kind: "draft" });
const creationComplete = computed(() => creation.value.kind === "completed");
const createdTeam = computed(() => (creation.value.kind === "saved" || creation.value.kind === "completed" ? creation.value.team : null));
const draftLocked = computed(() => submitting.value || creation.value.kind !== "draft");
const invitationStep = useTemplateRef<HTMLElement>("invitationStep");
const showInvitationRecovery = ref(false);
const invitationError = ref("");
const submitError = ref("");
const joinError = ref("");
const joinedTeam = ref<TeamPerspectiveSummary | null>(null);
const joiningName = ref("");
const creating = computed(() => submitting.value && !joiningName.value && !joinedTeam.value);
const requestId = globalThis.crypto.randomUUID();
let creationStarted = false;
function startCreation() {
    if (!creationStarted) {
        creationStarted = true;
        logUserInteraction(AmplitudeEventNames.TEAM_CREATION_STARTED, creationAttribution.value);
    }
}
function cancelCreation() {
    if (creationStarted && !createdTeam.value && !joinedTeam.value && !submitting.value) {
        creationStarted = false;
        logUserInteraction(AmplitudeEventNames.TEAM_CREATION_CANCELLED, creationAttribution.value);
    }
}
onMounted(() => {
    if (showDetails.value) {
        startCreation();
    }
});
onBeforeUnmount(cancelCreation);
const validDraft = computed(() => !!name.value.trim() && name.value.trim().length <= 255 && !submitting.value && showDetails.value && !createdTeam.value);
const canCreate = computed(
    () => validDraft.value && (creation.value.kind === "pending" || members.value.length > 0 || (invitations.value.length > 0 && capabilities.value?.can_invite_accountless)),
);

watch(candidate, async (person) => {
    if (!person || draftLocked.value || selected.value.length >= 100) {
        return;
    }
    if (person.id !== creator.id && !members.value.some((member) => member.id === person.id) && !selectedEmail(person.email)) {
        selected.value.push({ kind: "member", person });
        logUserInteraction(AmplitudeEventNames.TEAM_MEMBER_SELECTED, { ...creationAttribution.value, team_member_count: members.value.length, selection_method: "search" });
    }
    await nextTick();
    candidate.value = null;
    query.value = "";
});

function removeSelection(selection: TeamSelection) {
    if (submitting.value || (creation.value.kind !== "draft" && !(creation.value.kind === "saved" && selection.kind === "invitation"))) {
        return;
    }
    selected.value = selected.value.filter((item) => selectionKey(item) !== selectionKey(selection));
}

function reviewSuggestion() {
    creationSource.value = TEAM_INTERACTION_SOURCE.CALENDAR_SUGGESTION;
    void reviewTeam();
}

function useSuggestion(suggestion: TeamRecommendation) {
    if (draftLocked.value) {
        return;
    }
    if (!name.value.trim()) {
        name.value = suggestion.name;
    }
    const selectedIds = new Set([creator.id, ...members.value.map((person) => person.id)]);
    for (const person of suggestion.members) {
        if (person.candidate) {
            if (!selectedIds.has(person.candidate.id) && !selectedEmail(person.candidate.email) && selected.value.length < 100) {
                selected.value.push({ kind: "member", person: person.candidate });
                selectedIds.add(person.candidate.id);
            }
        } else if (!unavailableMembers.value.some((member) => member.id === person.id)) {
            unavailableMembers.value.push(person);
        }
    }
    reviewSuggestion();
}

async function joinSuggestion(suggestion: ExistingTeamRecommendation) {
    if (submitting.value) {
        return;
    }
    const joinButton = document.activeElement instanceof HTMLButtonElement ? document.activeElement : null;
    submitting.value = true;
    emit("submitting", true);
    joinError.value = "";
    joiningName.value = suggestion.name;
    logUserInteraction(AmplitudeEventNames.TEAM_JOIN_STARTED, { source: TEAM_INTERACTION_SOURCE.TEAM_SUGGESTION, entry_point: source, team_id: suggestion.team_id });
    try {
        const { data: team, error } = await useFetch(suggestion.join_url, teamInteractionRequest(TEAM_INTERACTION_SOURCE.TEAM_SUGGESTION, source)).post({}).json<PersonTeam>();
        if (error.value || !team.value?.is_member) {
            joinError.value = t("peopleDirectory.teamSuggestions.joinError");
            submitting.value = false;
            return;
        }
        const summary = { id: team.value.id, name: team.value.name, url: team.value.url ?? teamUrlTemplate.replace("{team_id}", team.value.id) };
        joinedTeam.value = summary;
        emit("selected", summary);
    } catch {
        joinError.value = t("peopleDirectory.teamSuggestions.joinError");
        submitting.value = false;
    } finally {
        joiningName.value = "";
        emit("submitting", submitting.value);
        if (joinError.value && joinButton) {
            await nextTick();
            if (document.activeElement === document.body) {
                joinButton.focus();
            }
        }
    }
}

async function createTeam() {
    if (!canCreate.value) {
        return;
    }
    if (creation.value.kind === "draft") {
        creation.value = {
            kind: "pending",
            payload: { request_id: requestId, name: name.value.trim(), member_ids: members.value.map((member) => member.id) },
        };
    }
    if (creation.value.kind !== "pending") {
        return;
    }
    const pending = creation.value;
    submitting.value = true;
    emit("submitting", true);
    submitError.value = "";
    try {
        const { data: team, error, statusCode } = await useFetch(createUrl, teamInteractionRequest(creationSource.value, source)).post(pending.payload).json<TeamCreated>();
        if (error.value || !team.value) {
            submitError.value = error.value instanceof Error && error.value.message ? error.value.message : t("peopleDirectory.createTeam.createError");
            if (statusCode.value && statusCode.value >= 400 && statusCode.value < 500) {
                creation.value = { kind: "draft" };
            }
            return;
        }
        creation.value = { kind: "saved", team: { ...team.value, url: teamUrlTemplate.replace("{team_id}", team.value.id) } };
        await sendInvitations();
        if (!pendingInvitations.value.length) {
            finishCreation();
        } else {
            showInvitationRecovery.value = true;
        }
    } catch {
        submitError.value = t("peopleDirectory.createTeam.createError");
    } finally {
        submitting.value = creationComplete.value;
        if (!creationComplete.value) {
            selected.value = selected.value.filter((selection) => selection.kind === "member" || selection.status === "pending");
        }
        emit("submitting", submitting.value);
        await nextTick();
        invitationStep.value?.focus();
    }
}

async function sendInvitations() {
    if (creation.value.kind !== "saved") {
        return;
    }
    invitationError.value = "";
    for (const invitation of pendingInvitations.value) {
        if (!capabilities.value?.can_invite_accountless) {
            return;
        }
        logUserInteraction(AmplitudeEventNames.TEAM_INVITATION_STARTED, { source: creationSource.value, team_id: creation.value.team.id, is_resend: false });
        const payload: TeamInvitationCreateRequest = { email: invitation.email, request_id: invitation.requestId };
        try {
            const { data, error } = await useFetch(`/v1/teams/${creation.value.team.id}/invitations`, teamInteractionRequest(creationSource.value, source))
                .post(payload)
                .json<TeamInvitationCreated | TeamJoinLinkCreated>();
            if (error.value || !data.value) {
                invitationError.value = t("teamInvitations.remainingError");
                return;
            }
            if (data.value.status === "share_link") {
                capabilities.value = { can_add_members: true, can_invite_accountless: false, can_share_join_link: true };
                return;
            }
            invitation.status = "sent";
        } catch {
            invitationError.value = t("teamInvitations.remainingError");
            return;
        }
    }
}

async function retryInvitations() {
    if (submitting.value || creation.value.kind !== "saved") {
        return;
    }
    submitting.value = true;
    emit("submitting", true);
    try {
        await sendInvitations();
        if (!pendingInvitations.value.length) {
            finishCreation();
        }
    } finally {
        submitting.value = creationComplete.value;
        if (!creationComplete.value) {
            selected.value = selected.value.filter((selection) => selection.kind === "member" || selection.status === "pending");
        }
        emit("submitting", submitting.value);
    }
}

function finishCreation() {
    if (creation.value.kind !== "saved") {
        return;
    }
    const team = creation.value.team;
    creation.value = { kind: "completed", team };
    if (!submitting.value) {
        submitting.value = true;
        emit("submitting", true);
    }
    emit("selected", team);
}
</script>
